import { pxToRem } from '@/Client/Styles/pxToRem';
import { css } from '@emotion/react';
import { useParentValueOnceSettled } from '@kaiizen/kai-pro-api-react-state';
import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { LabeledInputSize, LabeledInputStyle } from './LabeledInputCommonTypes';
import { LabeledInputPresenter } from './LabeledInputPresenter';
import { LABELED_INPUT_SLOT_SIZE_STYLES } from './labeledInputSlotSizesLib';

/** A stepper field counts things, so it never goes below zero: typed, stepped, or arrowed. */
export const LABELED_INPUT_STEPPER_FLOOR = 0;

/** Width of the stacked +/− column a stepper field carries at its left edge. */
const STEPPER_COLUMN_WIDTH = 18;

/**
 * The +/− column: out of flow over the field's left edge, so nothing shifts, and hidden until the
 * field is hovered or focused. Stepper fields right-align their number to keep clear of it.
 */
const STEPPER_COLUMN_STYLE = css`
  label: STEPPER_COLUMN_STYLE;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: ${STEPPER_COLUMN_WIDTH}px;
  border-right: 1px solid var(--border2);
  background: var(--background);
  opacity: 0;
  transition: opacity 0.12s;

  [data-input-container]:hover &,
  [data-input-container]:focus-within & {
    opacity: 1;
  }
`;

const STEPPER_BUTTON_STYLE = css`
  label: STEPPER_BUTTON_STYLE;
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text2);
  font-family: inherit;
  font-size: ${pxToRem(11)};
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  transition: background 0.12s, color 0.12s;

  & + & {
    border-top: 1px solid var(--border2);
  }

  &:hover:not(:disabled) {
    background: var(--background3);
    color: var(--text);
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

/**
 * 2026-08-03: Deliberately a single template (not split across a `css={[...]}`
 * array merge) with the taskCardField override baked in via interpolation.
 * Emotion + stylis has a reproducible bug where combining this block's nested
 * `&::-webkit-outer-spin-button` selector with a separate array item's plain
 * declarations corrupts the compiled output: the sibling's declarations get
 * duplicated into an unscoped, selector-less `<style>` fragment, and the
 * `::placeholder` vendor-prefix expansion loses its class scoping entirely
 * (leaking globally to every input on the page).
 * Keep this as one template, and keep it as the LAST entry in any
 * `css={[...]}` array it's used in.
 */
const getInputElementStyle = (isTaskCardField: boolean) =>
  css`
  label: INPUT_ELEMENT_STYLE;
  width: 100%;
  border: none;
  padding: 0;
  background-color: transparent !important;
  min-height: ${isTaskCardField ? 0 : 42}px;
  outline: none;
  box-sizing: border-box;
  color: var(--text);

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  ${
    isTaskCardField && css`
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    color: inherit;

    &::placeholder {
      color: var(--text2);
      font-style: italic;
    }
  `
  }
`;

const TEXTAREA_STYLE = css`
  label: TEXTAREA_STYLE;
  width: 100%;
  border: none;
  padding: 0;
  color: var(--text);
  background-color: transparent !important;
  outline: none;
`;

const INPUT_STYLE_BLURRED = css`
  label: INPUT_STYLE_BLURRED;
`;

const INPUT_STYLE_FOCUSED = css`
  label: INPUT_STYLE_FOCUSED;
  color: var(--text);
`;

const INPUT_TEXT_ALIGN_RIGHT_STYLE = css`
  label: INPUT_TEXT_ALIGN_RIGHT_STYLE;
  text-align: right;
`;

const VALUE_TYPOGRAPHY_SIZES = {
  tightSmall: css`
    label: VALUE_TYPOGRAPHY_SIZES_TIGHT_SMALL;
    ${TYPOGRAPHY2.xSmall}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
  `,
  tiny: css`
    label: VALUE_TYPOGRAPHY_SIZES_TINY;
    ${TYPOGRAPHY2.xSmall}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
  `,
  small: css`
    label: VALUE_TYPOGRAPHY_SIZES_SMALL;
    ${TYPOGRAPHY2.xSmall}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
  `,
  medium: css`
    label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
    ${TYPOGRAPHY2.medium}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.medium)} !important;
  `,
  tightMedium: css`
    label: VALUE_TYPOGRAPHY_SIZES_MEDIUM;
    ${TYPOGRAPHY2.medium}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.medium)} !important;
  `,
  large: css`
    label: VALUE_TYPOGRAPHY_SIZES_LARGE;
    ${TYPOGRAPHY2.large}
    font-size: ${pxToRem(TYPOGRAPHY2.fontSize.large)} !important;
  `,
};

const LINE_HEIGHT_BY_SIZE = {
  tiny: TYPOGRAPHY2.fontSize.tiny,
  small: TYPOGRAPHY2.fontSize.small,
  tightSmall: TYPOGRAPHY2.fontSize.small,
  medium: TYPOGRAPHY2.fontSize.medium,
  tightMedium: TYPOGRAPHY2.fontSize.medium,
  large: TYPOGRAPHY2.fontSize.large,
};

const UNIT_STYLE = css`
  label: UNIT_STYLE;
  color: var(--text2);
`;

/** A task card field's container sets no gap (its stepper column sits flush), so a trailing unit spaces itself off the value. */
const UNIT_AFTER_TASK_CARD_FIELD_STYLE = css`
  label: UNIT_AFTER_TASK_CARD_FIELD_STYLE;
  margin-left: ${LAYOUT2.spacingHalf}px;
`;

/** re-create LabeledInput using LabeledInputPresenter - and without the pick-list option; we'll make a separate new component for that - This should just be a simple input field again. */
export type LabeledInput2Props = {
  title?: string;
  label?: string;
  value: string;
  unit?: string;
  placeholder?: string;
  type?: 'text' | 'tel' | 'email' | 'password' | 'number' | 'url' | 'search';
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  style?: LabeledInputStyle;
  size?: LabeledInputSize;
  width?: string;
  onChange?: (value: string) => void;
  onUpdate?: (value: string) => void;
  onEscape?: () => void;
  onEnter?: () => void;
  backgroundColor?: string;
  delay?: number;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  optional?: boolean;
  tabIndex?: number;
  frameColor?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
  /** Shows normal styling while blocking edits (avoids disabled fade). */
  readOnly?: boolean;
  /** Numbers and amounts right-align so their digits line up down a column; text stays left. */
  textAlign?: 'left' | 'right';
  /**
   * Makes a number field a stepper: a stacked +/− column at its left edge adds or subtracts this
   * much (from zero when the field is empty), and the field floors at LABELED_INPUT_STEPPER_FLOOR.
   */
  step?: number;
};

const CHARACTER_COUNT_STYLE = css`
  label: CHARACTER_COUNT_STYLE;
  text-align: right;
  padding-top: 2px;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

const CHARACTER_COUNT_LIMIT_STYLE = css`
  label: CHARACTER_COUNT_LIMIT_STYLE;
  color: var(--red);
`;

const INPUT_CONTAINER_STYLE_DISABLED = css`
  label: INPUT_CONTAINER_STYLE_DISABLED;
  opacity: 0.67;
  font-style: italic;
  cursor: default;
`;

export const LabeledInput = ({
  title,
  label,
  value: initialValue,
  placeholder,
  type = 'text',
  autoComplete,
  autoFocus = false,
  disabled = false,
  readOnly = false,
  unit,
  style = 'normal',
  size = 'medium',
  width,
  delay = 500,
  onChange,
  onUpdate,
  onEscape,
  onEnter,
  backgroundColor,
  required = false,
  multiline = false,
  rows = 5,
  optional = false,
  tabIndex,
  frameColor,
  maxLength,
  showCharacterCount,
  textAlign,
  step,
}: LabeledInput2Props) => {
  const domDisabled = disabled && !readOnly;
  if (initialValue === 'null') throw new Error("initialValue cannot be 'null'");
  const [localValue, setLocalValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;
  // Read when deciding whether to show the parent's value, which must not wait for a re-render to
  // see a focus change.
  const isFocusedRef = useRef(false);

  const showValue = (newValue: string) => {
    localValueRef.current = newValue;
    setLocalValue(newValue);
  };

  const debouncedUpdate = useDebouncedCallback(
    (updatedValue: string) => onUpdate?.(updatedValue),
    delay,
  );

  // Follow the parent's value - except while the user is typing in a field that autosaves: a save
  // can come back formatted ("12." as "$12.00") or after more keystrokes ("abc" over "abcd"), and
  // showing it would erase what they typed (KAIDEV-4331). Nor while stepper clicks are still coming in:
  // the parent passes back earlier clicks' saves after later clicks (KAIDEV-4328). The parent's value
  // takes over once the user leaves the field, or the clicks settle. A field driven only through
  // onChange never gets a late save back, so a change while focused there is the parent's decision
  // (e.g. clearing the field after Enter).
  const autosaves = !!onUpdate;
  const { markEdited, showParentValueIfSettled } = useParentValueOnceSettled(
    initialValue,
    parentValue => {
      if (parentValue === localValueRef.current) return;
      console.log('initialValue changed - updating localValue to', parentValue, 'from', localValueRef.current);
      showValue(parentValue);
    },
    () => autosaves && (isFocusedRef.current || debouncedUpdate.isPending()),
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (readOnly) return;
    const newValue = event.target.value;
    onChange?.(newValue);
    showValue(newValue);
    debouncedUpdate(newValue);
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
    setIsFocused(true);
    if (readOnly) return;
    if (multiline) {
      textareaRef.current?.select();
    } else {
      inputRef.current?.select();
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setIsFocused(false);
    if (debouncedUpdate.isPending()) {
      // The parent's value takes over when this last save comes back.
      debouncedUpdate.flush();
    } else {
      // Everything typed was already saved, so the parent's value - formatted, or changed while the
      // user was here - is current.
      showParentValueIfSettled();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      onEscape?.();
    } else if (event.key === 'Enter' && !multiline) {
      onUpdate?.(localValue);
      onEnter?.();
    }
  };

  const unitBefore = style !== 'taskCardField' && (size === 'tiny' || unit === 'usd');

  // A stepper writes through at once rather than waiting out the typing debounce: a click is a decision.
  const hasStepper = step !== undefined && !multiline && !domDisabled && !readOnly;
  const getSteppedValue = (fromValue: string) => Math.max(LABELED_INPUT_STEPPER_FLOOR, parseFloat(fromValue) || 0);
  const steppedValue = hasStepper ? getSteppedValue(localValue) : 0;
  const stepBy = (direction: 1 | -1) => {
    // From the ref, so rapid clicks handled before a re-render still count from the latest one.
    const next = String(Math.max(LABELED_INPUT_STEPPER_FLOOR, getSteppedValue(localValueRef.current) + direction * step!));
    debouncedUpdate.cancel();
    showValue(next);
    markEdited();
    onUpdate?.(next);
  };

  return (
    <>
      <LabeledInputPresenter
        width={width}
        style={style}
        size={size}
        backgroundColor={backgroundColor}
        frameColor={frameColor}
        onClick={() => {}}
        title={title || ''}
        isFocused={isFocused}
        label={label}
        optional={optional}
        unitBefore={unitBefore}
        disabled={domDisabled}
      >
        {unitBefore && unit && <div css={[UNIT_STYLE, VALUE_TYPOGRAPHY_SIZES[size]]}>{unit === 'usd' ? '$' : `${unit}: `}</div>}
        {multiline
          ? (
            <textarea
              ref={textareaRef}
              css={[
                style !== 'taskCardField' && VALUE_TYPOGRAPHY_SIZES[size],
                TEXTAREA_STYLE,
                isFocused ? INPUT_STYLE_FOCUSED : INPUT_STYLE_BLURRED,
                domDisabled && INPUT_CONTAINER_STYLE_DISABLED,
                multiline && css`
              label: TEXTAREA_ROWS_STYLE;
              height: auto !important;
              padding-top: ${LAYOUT2.spacing1}px;
              padding-bottom: ${LAYOUT2.spacing1}px;
              min-height: ${pxToRem(rows * LINE_HEIGHT_BY_SIZE[size])} !important;`,
              ]}
              rows={rows}
              value={localValue}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              disabled={domDisabled}
              readOnly={readOnly}
              data-size={size}
              required={required}
              tabIndex={tabIndex}
              maxLength={maxLength}
            />
          )
          : (
            <input
              ref={inputRef}
              css={[
                LABELED_INPUT_SLOT_SIZE_STYLES[size],
                style !== 'taskCardField' && VALUE_TYPOGRAPHY_SIZES[size],
                isFocused ? INPUT_STYLE_FOCUSED : INPUT_STYLE_BLURRED,
                domDisabled && INPUT_CONTAINER_STYLE_DISABLED,
                getInputElementStyle(style === 'taskCardField'),
                textAlign === 'right' && INPUT_TEXT_ALIGN_RIGHT_STYLE,
              ]}
              type={type}
              value={localValue}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              disabled={domDisabled}
              readOnly={readOnly}
              data-size={size}
              required={required}
              tabIndex={tabIndex}
              maxLength={maxLength}
              step={step}
              min={step === undefined ? undefined : LABELED_INPUT_STEPPER_FLOOR}
            />
          )}
        {!unitBefore && unit && (
          <div css={[UNIT_STYLE, style === 'taskCardField' ? UNIT_AFTER_TASK_CARD_FIELD_STYLE : VALUE_TYPOGRAPHY_SIZES[size]]}>{unit}</div>
        )}
        {hasStepper && (
          <div css={STEPPER_COLUMN_STYLE}>
            <button css={STEPPER_BUTTON_STYLE} type='button' aria-label='increment' onClick={() => stepBy(1)}>
              +
            </button>
            <button
              css={STEPPER_BUTTON_STYLE}
              type='button'
              aria-label='decrement'
              disabled={steppedValue <= LABELED_INPUT_STEPPER_FLOOR}
              onClick={() => stepBy(-1)}
            >
              −
            </button>
          </div>
        )}
      </LabeledInputPresenter>
      {showCharacterCount && maxLength && (
        <div css={[CHARACTER_COUNT_STYLE, localValue.length >= maxLength && CHARACTER_COUNT_LIMIT_STYLE]}>
          {localValue.length} / {maxLength}
        </div>
      )}
    </>
  );
};

type FormikLabeledInputProps = Omit<LabeledInput2Props, 'value' | 'onChange' | 'onUpdate'> & {
  name: string;
};

export const FormikLabeledInput = (labeledInputProps: FormikLabeledInputProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  return (
    <LabeledInput
      {...labeledInputProps}
      value={values[labeledInputProps.name] || ''}
      onChange={value => setFieldValue(labeledInputProps.name, value)}
    />
  );
};
